import React from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

// Local calendar day as YYYY-MM-DD. toISOString() would convert to UTC and
// hand back "yesterday" for anyone west of Greenwich after 6pm, so en-CA
// (which formats as YYYY-MM-DD) is used against the browser's own zone.
const dayKey = (d) => d.toLocaleDateString('en-CA');

const shift = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

const startOfMonth = (offsetMonths = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths);
  return d;
};

const endOfMonth = (offsetMonths = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths + 1);
  d.setDate(0);
  return d;
};

export const PRESETS = [
  { id: 'today',      label: 'Today',          range: () => ({ startDate: dayKey(new Date()), endDate: dayKey(new Date()) }) },
  { id: 'yesterday',  label: 'Yesterday',      range: () => ({ startDate: dayKey(shift(-1)), endDate: dayKey(shift(-1)) }) },
  { id: 'last7',      label: 'Last 7 days',    range: () => ({ startDate: dayKey(shift(-6)), endDate: dayKey(new Date()) }) },
  { id: 'last14',     label: 'Last 14 days',   range: () => ({ startDate: dayKey(shift(-13)), endDate: dayKey(new Date()) }) },
  { id: 'last30',     label: 'Last 30 days',   range: () => ({ startDate: dayKey(shift(-29)), endDate: dayKey(new Date()) }) },
  { id: 'thisMonth',  label: 'This month',     range: () => ({ startDate: dayKey(startOfMonth(0)), endDate: dayKey(new Date()) }) },
  { id: 'lastMonth',  label: 'Last month',     range: () => ({ startDate: dayKey(startOfMonth(-1)), endDate: dayKey(endOfMonth(-1)) }) },
  { id: 'last90',     label: 'Last 90 days',   range: () => ({ startDate: dayKey(shift(-89)), endDate: dayKey(new Date()) }) },
  { id: 'ytd',        label: 'Year to date',   range: () => ({ startDate: `${new Date().getFullYear()}-01-01`, endDate: dayKey(new Date()) }) },
];

export const defaultRange = () => PRESETS.find((p) => p.id === 'last7').range();

const DateRangePicker = ({ startDate, endDate, onChange, onRun, isLoading, maxRangeDays = 400 }) => {
  const today = dayKey(new Date());

  const dayCount = (() => {
    const a = Date.parse(`${startDate}T00:00:00Z`);
    const b = Date.parse(`${endDate}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    return Math.round((b - a) / 86400000) + 1;
  })();

  const invalid = dayCount === null || dayCount < 1;
  const tooLong = dayCount !== null && dayCount > maxRangeDays;

  const activePreset = PRESETS.find((p) => {
    const r = p.range();
    return r.startDate === startDate && r.endDate === endDate;
  });

  const applyPreset = (preset) => {
    const r = preset.range();
    onChange(r);
  };

  return (
    <div className="flex-shrink-0 bg-slate-800 border border-slate-700 rounded-lg p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="void-start-date" className="block text-xs font-medium text-slate-400 mb-1">
            Ship date from
          </label>
          <div className="relative">
            <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="void-start-date"
              type="date"
              value={startDate}
              max={today}
              onChange={(e) => onChange({ startDate: e.target.value, endDate })}
              className="bg-slate-900 border border-slate-600 text-slate-200 rounded-md pl-9 pr-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark]"
            />
          </div>
        </div>

        <div>
          <label htmlFor="void-end-date" className="block text-xs font-medium text-slate-400 mb-1">
            Ship date to
          </label>
          <div className="relative">
            <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="void-end-date"
              type="date"
              value={endDate}
              max={today}
              onChange={(e) => onChange({ startDate, endDate: e.target.value })}
              className="bg-slate-900 border border-slate-600 text-slate-200 rounded-md pl-9 pr-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark]"
            />
          </div>
        </div>

        <button
          onClick={onRun}
          disabled={isLoading || invalid || tooLong}
          className={`px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
            isLoading || invalid || tooLong
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              <span>Running…</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 -rotate-90" />
              <span>Run report</span>
            </>
          )}
        </button>

        {dayCount !== null && !invalid && !tooLong && (
          <span className="text-xs text-slate-500 pb-2.5">
            {dayCount} day{dayCount === 1 ? '' : 's'} selected
          </span>
        )}
        {invalid && (
          <span className="text-xs text-red-400 pb-2.5">The start date must be on or before the end date.</span>
        )}
        {tooLong && (
          <span className="text-xs text-red-400 pb-2.5">
            {dayCount} days is beyond the {maxRangeDays}-day limit. Narrow the range.
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-700/60">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            onClick={() => applyPreset(preset)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activePreset?.id === preset.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DateRangePicker;
